import type { Document, DocumentVersion } from "@models";
import { getVersionUserDisplayName } from "src/tiptap/components/tiptap-templates/simple/version-authoring";
import type {
  SimpleEditorVersionCardAuthor,
  SimpleEditorVersionCardAuthorByVersion,
  SimpleEditorVersionCardRestorableByVersion,
  SimpleEditorVersionCardStatus,
  SimpleEditorVersionCardTitleByVersion,
} from "src/tiptap/components/tiptap-templates/simple/version-history";
import {
  getDocVersionRevisionNumber,
  isDocRevisionLive,
} from "src/utils/documents";

export type TiptapVersionCardMapping = {
  backendDocumentVersionId: string;
  documentVersion: DocumentVersion;
  status: Exclude<SimpleEditorVersionCardStatus, null>;
};

type TiptapVersionCardMappingOptions = {
  document?: Pick<Document, "versions"> | null;
  latestTiptapVersion: number | null;
  pendingApprovalSnapshotBaselineVersion?: number | null;
};

const hasTiptapVersion = (
  documentVersion: DocumentVersion
): documentVersion is DocumentVersion & { tiptapVersion: number } =>
  typeof documentVersion.tiptapVersion === "number";

const hasApprovers = (documentVersion: DocumentVersion) =>
  (documentVersion.documentVersionApprover ?? []).length > 0;

const getCurrentDocumentVersionStatus = (
  documentVersion: DocumentVersion
): Exclude<SimpleEditorVersionCardStatus, null> =>
  documentVersion.readyForApproval === true && documentVersion.revision === null
    ? "Routing"
    : "Editable";

const getDocumentVersionAuthor = (
  documentVersion: DocumentVersion
): SimpleEditorVersionCardAuthor | null => {
  const user = documentVersion.createdByUser;

  if (!user) {
    return null;
  }

  return {
    id: user.id,
    name: getVersionUserDisplayName(user),
  };
};

const createMapping = ({
  documentVersion,
  status,
}: {
  documentVersion: DocumentVersion;
  status: Exclude<SimpleEditorVersionCardStatus, null>;
}): TiptapVersionCardMapping => ({
  backendDocumentVersionId: documentVersion.id,
  documentVersion,
  status,
});

const getLatestPinnedTiptapVersion = (documentVersions: DocumentVersion[]) =>
  documentVersions.reduce<number | null>((latestPinnedVersion, version) => {
    if (!hasTiptapVersion(version)) {
      return latestPinnedVersion;
    }

    return latestPinnedVersion === null ||
      version.tiptapVersion > latestPinnedVersion
      ? version.tiptapVersion
      : latestPinnedVersion;
  }, null);

export const getTiptapVersionCardMappings = ({
  document,
  latestTiptapVersion,
  pendingApprovalSnapshotBaselineVersion = null,
}: TiptapVersionCardMappingOptions): ReadonlyMap<
  number,
  TiptapVersionCardMapping
> => {
  const mappings = new Map<number, TiptapVersionCardMapping>();
  const documentVersions = document?.versions ?? [];

  documentVersions.forEach((documentVersion) => {
    if (
      typeof documentVersion.revision === "number" &&
      hasTiptapVersion(documentVersion)
    ) {
      mappings.set(
        documentVersion.tiptapVersion,
        createMapping({
          documentVersion,
          status: isDocRevisionLive(documentVersion, documentVersions)
            ? "Live"
            : "Archived",
        })
      );
    }
  });

  const latestDocumentVersion = documentVersions[0] ?? null;
  const isWaitingForApprovalSnapshot =
    pendingApprovalSnapshotBaselineVersion !== null &&
    latestTiptapVersion !== null &&
    latestTiptapVersion <= pendingApprovalSnapshotBaselineVersion &&
    latestDocumentVersion !== null &&
    hasApprovers(latestDocumentVersion);

  if (
    latestDocumentVersion?.revision === null &&
    latestTiptapVersion !== null &&
    isWaitingForApprovalSnapshot
  ) {
    mappings.set(
      latestTiptapVersion,
      createMapping({
        documentVersion: latestDocumentVersion,
        status: "Editable",
      })
    );
  } else if (
    latestDocumentVersion?.revision === null &&
    latestTiptapVersion !== null
  ) {
    mappings.set(
      latestTiptapVersion,
      createMapping({
        documentVersion: latestDocumentVersion,
        status: getCurrentDocumentVersionStatus(latestDocumentVersion),
      })
    );
  }

  return mappings;
};

export const getTiptapVersionCardStatusByVersion = (
  options: TiptapVersionCardMappingOptions
): ReadonlyMap<number, SimpleEditorVersionCardStatus> => {
  const mappings = getTiptapVersionCardMappings(options);

  return new Map(
    Array.from(mappings, ([tiptapVersion, mapping]) => [
      tiptapVersion,
      mapping.status,
    ])
  );
};

export const getTiptapVersionCardRestorableByVersion = (
  options: TiptapVersionCardMappingOptions
): SimpleEditorVersionCardRestorableByVersion => {
  const mappings = getTiptapVersionCardMappings(options);

  return new Map(
    Array.from(mappings, ([tiptapVersion, mapping]) => [
      tiptapVersion,
      mapping.documentVersion.restorable,
    ])
  );
};

export const getTiptapVersionCardAuthorByVersion = (
  options: TiptapVersionCardMappingOptions
): SimpleEditorVersionCardAuthorByVersion => {
  const mappings = getTiptapVersionCardMappings(options);
  const documentVersions = options.document?.versions ?? [];
  const latestDocumentVersion = documentVersions[0] ?? null;
  const editableAuthor =
    latestDocumentVersion?.revision === null &&
    !hasTiptapVersion(latestDocumentVersion)
      ? getDocumentVersionAuthor(latestDocumentVersion)
      : null;
  const latestPinnedTiptapVersion =
    getLatestPinnedTiptapVersion(documentVersions);
  const editableSnapshotStartVersion =
    latestPinnedTiptapVersion === null ? 0 : latestPinnedTiptapVersion + 1;
  const authorByVersion = new Map(
    Array.from(mappings)
      .map(
        ([tiptapVersion, mapping]) =>
          [
            tiptapVersion,
            getDocumentVersionAuthor(mapping.documentVersion),
          ] as const
      )
      .filter(
        (entry): entry is readonly [number, SimpleEditorVersionCardAuthor] =>
          entry[1] !== null
      )
  );

  if (editableAuthor && options.latestTiptapVersion !== null) {
    for (
      let tiptapVersion = editableSnapshotStartVersion;
      tiptapVersion <= options.latestTiptapVersion;
      tiptapVersion += 1
    ) {
      if (!authorByVersion.has(tiptapVersion)) {
        authorByVersion.set(tiptapVersion, editableAuthor);
      }
    }
  }

  return authorByVersion;
};

export const getTiptapVersionCardTitleByVersion = (
  options: TiptapVersionCardMappingOptions
): SimpleEditorVersionCardTitleByVersion => {
  const mappings = getTiptapVersionCardMappings(options);
  const documentVersions = options.document?.versions ?? [];

  return new Map(
    Array.from(mappings)
      .filter(
        ([, mapping]) =>
          typeof mapping.documentVersion.revision === "number" ||
          mapping.status === "Routing"
      )
      .map(([tiptapVersion, mapping]) => [
        tiptapVersion,
        `Revision ${getDocVersionRevisionNumber(
          mapping.documentVersion,
          documentVersions
        )}`,
      ])
  );
};

export const shouldLockTiptapVersionPreview = ({
  latestTiptapVersion,
  status,
  version,
}: {
  latestTiptapVersion: number | null;
  status: SimpleEditorVersionCardStatus;
  version: number | null;
}) =>
  latestTiptapVersion !== null &&
  version !== null &&
  version === latestTiptapVersion &&
  (status === "Routing" || status === "Live");

export const getBackendDocumentVersionForTiptapVersion = ({
  selectedTiptapVersion,
  ...options
}: TiptapVersionCardMappingOptions & {
  selectedTiptapVersion: number | null;
}): DocumentVersion | null => {
  if (selectedTiptapVersion === null) {
    return null;
  }

  return (
    getTiptapVersionCardMappings(options).get(selectedTiptapVersion)
      ?.documentVersion ?? null
  );
};
